const getTranslateY = (transform: string) => {
    if (transform === "none" || typeof DOMMatrixReadOnly === "undefined") {
        return 0;
    }
    try {
        return new DOMMatrixReadOnly(transform).m42;
    } catch {
        return 0;
    }
};

/**
 * Measures the tallest visible "sticky checkout actions" bar (Cart, Checkout Shipping,
 * Checkout Review & Submit) from its own box instead of the viewport. iOS layout/visual
 * viewports disagree while the browser toolbars animate during scroll, which made
 * viewport-relative measurements collapse to 0 mid-scroll.
 */
export const measureStickyCheckoutOffset = () => {
    const stickyActions = Array.from(document.querySelectorAll<HTMLElement>('[data-sticky-checkout-actions="true"], .sticky-actions'));
    return stickyActions.reduce((maxHeight, stickyAction) => {
        const rect = stickyAction.getBoundingClientRect();
        // The bars keep data-sticky-checkout-actions="true" while hidden by breakpoint styles
        // (display: none collapses the rect) or slid away with translateY (top past the fold).
        if (rect.height === 0 || rect.width === 0) {
            return maxHeight;
        }
        const style = window.getComputedStyle(stickyAction);
        if (style.visibility === "hidden" || style.opacity === "0") {
            return maxHeight;
        }
        const visibleHeight = rect.height - Math.max(getTranslateY(style.transform), 0);
        if (visibleHeight <= 0) {
            return maxHeight;
        }
        const bottom = Number.parseFloat(style.bottom);
        return Math.max(maxHeight, visibleHeight + (Number.isFinite(bottom) ? bottom : 0));
    }, 0);
};

/**
 * Invokes `onChange` with the current sticky checkout offset whenever a sticky bar appears,
 * disappears, resizes, or the page scrolls/resizes. Returns a cleanup function.
 */
export const observeStickyCheckoutOffset = (onChange: (offset: number) => void) => {
    const update = () => onChange(measureStickyCheckoutOffset());

    const resizeObserver = new ResizeObserver(update);
    const observeStickyActions = () => {
        document
            .querySelectorAll<HTMLElement>('[data-sticky-checkout-actions="true"], .sticky-actions')
            .forEach(stickyAction => resizeObserver.observe(stickyAction));
    };

    const mutationObserver = new MutationObserver(() => {
        update();
        window.setTimeout(update, 350);
        observeStickyActions();
    });
    mutationObserver.observe(document.body, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ["data-sticky-checkout-actions"],
    });
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    observeStickyActions();
    update();

    return () => {
        mutationObserver.disconnect();
        resizeObserver.disconnect();
        window.removeEventListener("scroll", update);
        window.removeEventListener("resize", update);
    };
};
